import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle, Calendar } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function NinetyDayPlan() {
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto w-full" data-id="element-91">
      <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-92">
        <Calendar className="w-12 h-12 text-sierra-blue mx-auto mb-6" data-id="element-93" />
        <h2 className="text-4xl font-bold text-sierra-dark mb-4" data-id="element-94">
          Appendix: 90-Day Plan
        </h2>
      </motion.div>

      <motion.div variants={fadeUp} className="space-y-8" data-id="element-95">
        {[{
        period: 'Days 0–30',
        title: 'Ramp & Ship',
        desc: 'Ramp on product + trust model; shadow top deals; rebuild demo talk tracks; ship one telco-ready hero demo script.'
      }, {
        period: 'Days 31–60',
        title: 'Systematise',
        desc: 'Publish 2–3 reusable demo playbooks + POC contract template; introduce lightweight deal review cadence.'
      }, {
        period: 'Days 61–90',
        title: 'Lead & Scale',
        desc: 'Co-lead strategic deals; tighten discovery→demo conversion; partner with Product on field-driven improvements.'
      }].map((phase, i) => <div key={i} className="bg-white p-8 rounded-2xl sierra-shadow border border-gray-100 flex items-start gap-6" data-id="element-96">
            <div className="px-4 py-2 bg-green-50 text-sierra-blue text-sm font-bold rounded-full uppercase tracking-wider whitespace-nowrap" data-id="element-97">
              {phase.period}
            </div>
            <div data-id="element-98">
              <h3 className="text-lg font-semibold text-sierra-dark mb-2" data-id="element-99">
                {phase.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-100">{phase.desc}</p>
            </div>
          </div>)}
      </motion.div>
    </motion.div>;
}
export function AppendixQA() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const qaItems = [{
    q: 'How does the agent handle PII and data residency across regions?',
    a: 'PII is masked and redacted by default in every conversation. Sierra is ISO 27001 and ISO 42001 certified. Data residency is configurable per deployment - we can ensure data stays within region. All actions and decisions are audit-logged.'
  }, {
    q: "What integrations are required and what's the implementation timeline?",
    a: 'Sierra integrates natively with CRM (Salesforce), ITSM (ServiceNow), and custom APIs via composable tool skills. Typical launch is 4-6 weeks for an initial journey. No heavy lift - we work alongside your existing stack, not replace it.'
  }, {
    q: "How do you measure ROI and prove deflection isn't harming CSAT?",
    a: "We track containment rate, resolution rate, CSAT per conversation, repeat contact rate, and handle time. Sierra's insights loop surfaces trends automatically. Customers like SoFi saw +33 NPS with 61% containment - deflection and satisfaction improve together."
  }, {
    q: "What happens when the agent fails or can't resolve an issue?",
    a: "The agent degrades gracefully: it creates a case with full context (summary, actions taken, tool outputs), schedules a callback, and preserves the customer's current state. No irreversible action is taken during uncertainty. Sierra calls this 'safe failure by design'."
  }, {
    q: 'How does escalation work - what context does the human agent receive?',
    a: 'A structured context package transfers: conversation summary, every action taken, tool call results, guardrails fired, and a recommended next step. The human agent picks up with full situational awareness - no customer repetition.'
  }, {
    q: 'How do you govern agent behaviour and prevent drift?',
    a: "Every Sierra agent is monitored by supervisory agents (Sierra calls them 'Jiminy Crickets') that check each response against policy, brand, and safety rules. Input filtering catches prompt injection; output interception audits every action. Post-conversation reviews surface defects automatically."
  }, {
    q: 'Can the agent support multiple languages and regional variations?',
    a: 'Yes. Sierra deploys a single agent across chat, voice, and WhatsApp with multi-language support. Regional policy variations (e.g. different return windows, regulatory requirements) are handled via composable skills with region-specific guardrails.'
  }, {
    q: 'What does the testing and simulation framework look like?',
    a: "Sierra's Agent OS includes Simulations and Voice Sims - tens of thousands of synthetic conversations run daily against mock personas before any agent change reaches customers. Combined with τ-bench evaluations, this ensures reliability at scale before release."
  }];
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto w-full" data-id="element-101">
      <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-102">
        <HelpCircle className="w-12 h-12 text-sierra-blue mx-auto mb-6" data-id="element-103" />
        <h2 className="text-4xl font-bold text-sierra-dark mb-4" data-id="element-104">
          Appendix: Detailed Q&A
        </h2>
      </motion.div>

      <motion.div variants={fadeUp} className="space-y-4" data-id="element-105">
        {qaItems.map((item, i) => <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden sierra-shadow" data-id="element-106">
            <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors" data-id="element-107">
              <span className="font-semibold text-sierra-dark pr-8" data-id="element-108">
                {item.q}
              </span>
              <ChevronDown className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} data-id="element-109" />
            </button>
            <AnimatePresence data-id="element-110">
              {openIndex === i && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} data-id="element-111">
                  <div className="p-5 pt-0 text-gray-600 border-t border-gray-100 mt-2" data-id="element-112">
                    {item.a}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </motion.div>
    </motion.div>;
}